import React, { Dispatch, SetStateAction } from 'react';
import { Drawer } from 'components/drawer';
import PaySDKLoader from './pay_sdk_loader';
import accessLocalStorage from 'services/localStorage';
import useTicketOperations from 'state/ticket/hooks/useTicketOperations';
import { ToastType } from 'components/toasts/container';

export interface IVirtualTerminalForm {
  originalAmount: number;
  taxes: number;
  discount: number;
  rewardDollars: number;
  authToken: string;
  entityId: string;
  locationId?: string;
  cashDrawerName?: string;
  processorName?: string;
  ticketId?: string;
  entityName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  employeeReferenceId?: string;
  ticketAlphaNumId?: string;
  isSelltreezTicket?: boolean;
  ticketStatus?: string;
  orderType?: string;
}

interface IProps {
  showPaySDK: boolean;
  setShowPaySDK: Dispatch<SetStateAction<boolean>>;
  paymentData: IVirtualTerminalForm;
  resetVirtualTerminal?: () => void;
  entityName?: string;
  dispensaryShortName?: string;
  isSellTreezEntity?: boolean;
  onShowToast?: (message: string, type: ToastType) => void;
}

interface callBackEvent {
  eventType: string; // Type of event
  processorName?: string; // Payment provider name
  paymentMethod?: string;
  userMessage?: string; // User friendly Message
  data?: Record<string, any>; // Event-specific data
}

const PaymentCollectionDrawer = ({
  showPaySDK,
  setShowPaySDK,
  paymentData,
  resetVirtualTerminal,
  entityName,
  dispensaryShortName,
  isSellTreezEntity,
  onShowToast,
}: IProps): JSX.Element => {
  const { completeTicket, saveTicket } = useTicketOperations();

  const onFormSubmit = () => {
    return;
  };

  const closeSDKHandler = (delay: number) => {
    setTimeout(() => {
      setShowPaySDK(false);
      resetVirtualTerminal?.();
    }, delay);
  };

  /**
   * Show toast notification via parent component
   */
  const showNotification = (message: string, type: ToastType) => {
    if (onShowToast) {
      onShowToast(message, type);
    }
  };

  /**
   * Handle Save Ticket API call
   * Wrapped with error handling to prevent component crashes
   */
  const handleSaveTicket = async () => {
    if (!isSellTreezEntity || !paymentData.ticketId) {
      closeSDKHandler(0);
      return;
    }

    try {
      await saveTicket({
        ticketId: paymentData.ticketId,
      });

      showNotification('Ticket saved successfully', ToastType.success);
      closeSDKHandler(1000);
    } catch (error: any) {
      console.error('Error saving ticket:', error);
      const errorMsg = error?.message || 'Failed to save ticket. Please try again.';
      showNotification(errorMsg, ToastType.error);
      closeSDKHandler(1000);
    }
  };

  /**
   * Handle Complete Ticket API call
   * Wrapped with error handling to prevent component crashes
   */
  const handleCompleteTicket = async () => {
    if (!isSellTreezEntity || !paymentData.ticketId) {
      closeSDKHandler(0);
      return;
    }

    try {
      await completeTicket({
        ticketId: paymentData.ticketId,
      });

      showNotification('Ticket completed successfully', ToastType.success);
      closeSDKHandler(1000);
    } catch (error: any) {
      console.error('Error completing ticket:', error);
      const errorMsg = error?.message || 'Failed to complete ticket. Please try again.';
      showNotification(errorMsg, ToastType.error);
      closeSDKHandler(1000);
    }
  };

  const handlePaymentResponse = (event: callBackEvent) => {
    // Handle ticket operations first (applies to all payment methods)
    if (event?.eventType === 'PAYMENT_TICKET_SAVED') {
      // Call API to save ticket in SellTreez - wrap in error boundary
      handleSaveTicket().catch((err) => {
        console.error('Unhandled error in handleSaveTicket:', err);
      });
      return;
    }

    if (event?.eventType === 'PAYMENT_TICKET_COMPLETED') {
      // Call API to complete ticket in SellTreez - wrap in error boundary
      handleCompleteTicket().catch((err) => {
        console.error('Unhandled error in handleCompleteTicket:', err);
      });
      return;
    }

    // Handle common close/cancel events
    if (event?.eventType === 'PAYMENT_CLOSE' || event?.eventType === 'PAYMENT_NEW_SALES') {
      closeSDKHandler(0);
      return;
    }

    // Handle payment success events based on payment method
    if (event?.paymentMethod === 'ACH') {
      if (event?.eventType === 'PAYMENT_CAPTURED') {
        if (!paymentData.isSelltreezTicket) {
          closeSDKHandler(7000);
        }
      }
    } else {
      // For non-ACH methods (card, cash, etc.)
      if (event?.eventType === 'PAYMENT_APPROVED') {
        if (!paymentData.isSelltreezTicket) {
          closeSDKHandler(7000);
        }
      }
    }
  };

  // Format phone number for SDK by removing dashes
  const formatPhoneForSDK = (phone?: string) => {
    if (!phone) return '';
    return phone.replace(/-/g, '');
  };

  return (
    <>
      <Drawer
        isOpen={showPaySDK}
        toggle={() => {
          // resetVirtualTerminal?.();
          setShowPaySDK(false);
        }}
        title=" "
        hasSdk
      >
        <PaySDKLoader
          ticketAlphaNumId={paymentData?.ticketAlphaNumId}
          ticketId={paymentData?.ticketId}
          totalAmount={paymentData?.originalAmount || 0}
          taxAmount={paymentData?.taxes || 0}
          discountTotal={paymentData?.discount || 0}
          ticketItems={[]}
          entityId={paymentData?.entityId || ''}
          entityName={entityName}
          locationId={paymentData?.locationId}
          cashDrawerName={paymentData?.cashDrawerName}
          authTokenFactory={() => accessLocalStorage.getUser().accessToken}
          handlePaymentResponse={handlePaymentResponse}
          customer={{
            firstName: paymentData?.firstName,
            lastName: paymentData?.lastName,
            phone: formatPhoneForSDK(paymentData?.phone),
            email: paymentData?.email || null,
          }}
          employeeReferenceId={paymentData?.employeeReferenceId}
          dispensaryShortName={dispensaryShortName}
          isSelltreezTicket={paymentData?.isSelltreezTicket}
          ticketStatus={paymentData?.ticketStatus}
          orderType={paymentData?.orderType}
        />
      </Drawer>
    </>
  );
};

export default PaymentCollectionDrawer;
